import React, { useState, useEffect } from 'react';
import { FiPlus, FiMinus, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { fetchMainCategories } from '../../store/slices/mainCategorySlice';
import { fetchCategoriesByMainCategory } from '../../store/slices/categorySlice';

interface FilterOption {
    label: string;
    value: string;
}

interface FilterSectionProps {
    title: string;
    options: FilterOption[];
    onSelect: (value: string) => void;
    selectedValues: string[];
}

const FilterSection: React.FC<FilterSectionProps> = ({ title, options, onSelect, selectedValues }) => {
    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 900) {
                setIsOpen(false); // Collapsed on mobile by default
            } else {
                setIsOpen(true); // Expanded on desktop by default
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div className="filter-section">
            <div className="filter-header" onClick={() => setIsOpen(!isOpen)}>
                <span className="filter-title">{title}</span>
                {isOpen ? <FiMinus size={14} className="filter-icon" /> : <FiPlus size={14} className="filter-icon" />}
            </div>

            {isOpen && (
                <div className="filter-options">
                    {options.map((option) => (
                        <label
                            key={option.value}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#555',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={selectedValues.includes(option.value)}
                                onChange={() => onSelect(option.value)}
                                style={{ cursor: 'pointer' }}
                            />
                            {option.label}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

interface MainCategorySectionProps {
    mainCategory: any;
    categories: any[];
    hoveredCategories: Record<string, any[]>;
    selectedMainCategories: string[];
    selectedCategories: string[];
    onMainCategoryClick: (id: string) => void;
    onCategoryClick: (id: string) => void;
}

const MainCategorySection: React.FC<MainCategorySectionProps> = ({
    mainCategory,
    categories,
    hoveredCategories,
    selectedMainCategories,
    selectedCategories,
    onMainCategoryClick,
    onCategoryClick
}) => {
    const isActive = selectedMainCategories.includes(mainCategory._id!);

    const mcId = mainCategory._id!;
    const rawCategories = hoveredCategories[mcId] || categories.filter((c: any) => {
        const categoryMainId = c.main_category?._id || c.main_category || c.main_category_id;
        return categoryMainId === mcId;
    }) || [];
    const matchingCategories = [...rawCategories].sort((a: any, b: any) => (Number(a.priority) || 999) - (Number(b.priority) || 999));

    return (
        <div className="filter-section">
            <div className="filter-header" onClick={() => onMainCategoryClick(mainCategory._id!)}>
                <span className="filter-title">{mainCategory.name}</span>
                {isActive ? <FiMinus size={14} className="filter-icon" /> : <FiPlus size={14} className="filter-icon" />}
            </div>

            {isActive && (
                <div className="filter-options">
                    {matchingCategories.length === 0 ? (
                        <span style={{ fontSize: '13px', color: '#999', paddingLeft: '4px' }}>Loading...</span>
                    ) : (
                        matchingCategories.map((c) => {
                            const isCatActive = selectedCategories.includes(c._id!);
                            return (
                                <label
                                    key={c._id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        color: isCatActive ? 'var(--color-primary)' : '#555',
                                        fontWeight: isCatActive ? '600' : 'normal',
                                        whiteSpace: 'nowrap',
                                        transition: 'color 0.2s ease'
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onCategoryClick(c._id!);
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isCatActive}
                                        readOnly
                                        style={{ cursor: 'pointer' }}
                                    />
                                    {c.name}
                                </label>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

interface SidebarFilterProps {
    onFilterChange: (filters: any) => void;
}

const SidebarFilter: React.FC<SidebarFilterProps> = ({ onFilterChange }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { mainCategories } = useSelector((state: RootState) => state.mainCategory);
    const { categories, hoveredCategories } = useSelector((state: RootState) => state.category);

    const [selectedMainCategories, setSelectedMainCategories] = useState<string[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

    const [selectedFilters, setSelectedFilters] = useState<any>({
        material: [],
        price: [],
        availability: []
    });

    useEffect(() => {
        if (mainCategories.length === 0) {
            dispatch(fetchMainCategories());
        }
    }, [dispatch, mainCategories.length]);

    const handleMainCategoryClick = (mcId: string) => {
        const isCurrentlyOpen = selectedMainCategories.includes(mcId);
        const nextMainCategories = isCurrentlyOpen
            ? selectedMainCategories.filter(id => id !== mcId)
            : [...selectedMainCategories, mcId];

        setSelectedMainCategories(nextMainCategories);

        if (!isCurrentlyOpen) {
            dispatch(fetchCategoriesByMainCategory(mcId) as any);
        }

        onFilterChange({
            ...selectedFilters,
            main_category: nextMainCategories,
            category_id: selectedCategories
        });
    };

    const handleCategoryClick = (catId: string) => {
        const nextCategories = selectedCategories.includes(catId)
            ? selectedCategories.filter((id) => id !== catId)
            : [...selectedCategories, catId];

        setSelectedCategories(nextCategories);

        onFilterChange({
            ...selectedFilters,
            main_category: selectedMainCategories,
            category_id: nextCategories
        });
    };

    const handleSelect = (section: string, value: string) => {
        const newValues = selectedFilters[section].includes(value)
            ? selectedFilters[section].filter((v: string) => v !== value)
            : [...selectedFilters[section], value];

        const newFilters = { ...selectedFilters, [section]: newValues };
        setSelectedFilters(newFilters);
        onFilterChange({
            ...newFilters,
            main_category: selectedMainCategories,
            category_id: selectedCategories
        });
    };
    // const materials = [
    //     { label: 'Sheesham Wood', value: 'sheesham' },
    //     { label: 'Mango Wood', value: 'mango' },
    //     { label: 'Teak Wood', value: 'teak' },
    //     { label: 'Engineered Wood', value: 'engineered' }
    // ];

    // const prices = [
    //     { label: 'Under Rs. 2,000', value: '0-2000' },
    //     { label: 'Rs. 2,000 - Rs. 5,000', value: '2000-5000' },
    //     { label: 'Rs. 5,000 - Rs. 10,000', value: '5000-10000' },
    //     { label: 'Over Rs. 10,000', value: '10000+' }
    // ];

    const availability = [
        { label: 'In Stock', value: 'in-stock' },
        { label: 'Out of Stock', value: 'out-of-stock' }
    ];

    return (
        <aside className="sidebar-filter-aside">
            <style>
                {`
                .sidebar-filter-aside {
                    width: 300px;
                    padding: 20px;
                    background-color: #fbfbfb;
                    height: fit-content;
                    border-right: 1px solid #eee;
                    position: sticky;
                    top: 140px;
                    max-height: calc(100vh - 160px);
                    overflow-y: auto;
                    align-self: flex-start;
                    margin-bottom: 30px;
                    box-sizing: border-box;
                }

                @media (max-width: 900px) {
                    .sidebar-filter-aside {
                        display: none !important;
                    }
                }

                .sidebar-breadcrumbs {
                    margin-bottom: 0px;
                }

                .breadcrumb-link {
                    display: block;
                    font-size: 14px;
                    color: #666;
                    margin-bottom: 8px;
                    text-decoration: none;
                }
                // .breadcrumb-link:hover {
                //     color: #000;
                // }
                .breadcrumb-active {
                    color: #000;
                    font-weight: 600;
                }

                 /* ── FilterSection Classes ── */
                 .filter-section {
                     border-bottom: 1px solid #eee;
                     padding: 16px 0;
                     position: relative;
                 }
                 .filter-header {
                     display: flex;
                     justify-content: space-between;
                     align-items: center;
                     cursor: pointer;
                 }
                 .filter-title {
                     font-size: 15px;
                     font-weight: 600;
                     color: #333;
                 }
                 .filter-icon {
                     color: #666;
                 }
                 .filter-options {
                     display: flex;
                     flex-direction: column;
                     gap: 8px;
                     margin-top: 12px;
                 }

                 /* ── Category Tree Classes ── */
                 .category-tree-container {
                     display: flex;
                     flex-direction: column;
                     gap: 6px;
                     margin-top: 12px;
                 }
                 .main-cat-item {
                     border-radius: 8px;
                     margin-bottom: 2px;
                     overflow: hidden;
                     transition: all 0.2s ease;
                 }
                 .main-cat-header {
                     display: flex;
                     justify-content: space-between;
                     align-items: center;
                     padding: 10px 12px;
                     cursor: pointer;
                     border-radius: 8px;
                     transition: all 0.2s ease;
                     background-color: transparent;
                 }
                 .main-cat-header:hover {
                     background-color: rgba(0, 164, 95, 0.04);
                 }
                 .main-cat-header.active {
                     background-color: rgba(0, 164, 95, 0.08);
                     border-left: 3px solid var(--color-primary);
                     border-top-left-radius: 0;
                     border-bottom-left-radius: 0;
                 }
                 .main-cat-name {
                     font-size: 14px;
                     font-weight: 600;
                     color: #444;
                     transition: color 0.2s ease;
                 }
                 .main-cat-header.active .main-cat-name {
                     color: var(--color-primary);
                     font-weight: 700;
                 }
                 .sub-cat-list {
                     padding: 6px 0 6px 20px;
                     display: flex;
                     flex-direction: column;
                     gap: 6px;
                     border-left: 1px dashed rgba(0, 164, 95, 0.2);
                     margin-left: 18px;
                     margin-top: 2px;
                     animation: slideDown 0.2s ease-out;
                 }
                 .sub-cat-item {
                     display: flex;
                     align-items: center;
                     gap: 8px;
                     padding: 6px 8px;
                     cursor: pointer;
                     border-radius: 4px;
                     font-size: 13px;
                     color: #666;
                     transition: all 0.2s ease;
                 }
                 .sub-cat-item:hover {
                     color: var(--color-primary);
                     background-color: rgba(0, 164, 95, 0.02);
                 }
                 .sub-cat-item.active {
                     color: var(--color-primary);
                     font-weight: 700;
                     background-color: rgba(0, 164, 95, 0.05);
                 }
                 .sub-cat-indicator {
                     width: 6px;
                     height: 6px;
                     border-radius: 50%;
                     background-color: #ccc;
                     transition: all 0.2s ease;
                 }
                 .sub-cat-item.active .sub-cat-indicator {
                     background-color: var(--color-primary);
                     transform: scale(1.2);
                 }
                 @keyframes slideDown {
                     from { opacity: 0; transform: translateY(-5px); }
                     to { opacity: 1; transform: translateY(0); }
                 }

                @media (max-width: 900px) {
                    .filter-section {
                        border-bottom: none !important;
                        padding: 0 !important;
                        flex-shrink: 0;
                    }
                    .filter-header {
                        border: 1px solid #e0e0e0 !important;
                        border-radius: 24px !important;
                        padding: 8px 16px !important;
                        background-color: #ffffff !important;
                        gap: 8px !important;
                        min-width: 100px !important;
                        justify-content: space-between !important;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.02) !important;
                    }
                    .filter-header:hover {
                        border-color: #43c8ca !important;
                    }
                    .filter-title {
                        font-size: 13px !important;
                        font-weight: 500 !important;
                        color: #444 !important;
                    }
                    .filter-options {
                        position: absolute !important;
                        top: calc(100% + 8px) !important;
                        left: 0 !important;
                        background: #ffffff !important;
                        border: 1px solid #eee !important;
                        border-radius: 8px !important;
                        box-shadow: 0 10px 25px rgba(0,0,0,0.12) !important;
                        padding: 14px !important;
                        z-index: 100 !important;
                        min-width: 220px !important;
                        margin-top: 0 !important;
                    }
                }
                `}
            </style>

            <div className="sidebar-breadcrumbs">
                <p className="breadcrumb-link">All Main Categories</p>
            </div>

            {/* Interactive Main Category Filter Sections */}
            {[...mainCategories]
                .sort((a: any, b: any) => (Number(a.priority) || 999) - (Number(b.priority) || 999))
                .map((mc) => (
                <MainCategorySection
                    key={mc._id}
                    mainCategory={mc}
                    categories={categories}
                    hoveredCategories={hoveredCategories}
                    selectedMainCategories={selectedMainCategories}
                    selectedCategories={selectedCategories}
                    onMainCategoryClick={handleMainCategoryClick}
                    onCategoryClick={handleCategoryClick}
                />
            ))}

            {/* <FilterSection
                title="Material"
                options={materials}
                onSelect={(v) => handleSelect('material', v)}
                selectedValues={selectedFilters.material}
            />
            <FilterSection
                title="Price"
                options={prices}
                onSelect={(v) => handleSelect('price', v)}
                selectedValues={selectedFilters.price}
            /> */}
            {/* <FilterSection
                title="Availability"
                options={availability}
                onSelect={(v) => handleSelect('availability', v)}
                selectedValues={selectedFilters.availability}
            /> */}
        </aside>
    );
};

export default SidebarFilter;
